import { deepEqualJson } from '@trackeat/shared/sync';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const RECOVERABLE_SYNC_CONTRACT_REASONS = [
  'temperature_field_not_allowed:operational_time',
  'cleaning_field_not_allowed:operator_name',
  'event_reference_business_mismatch:cleaning_products',
  'insert_failed:42501',
  'insert_failed',
] as const;

export type RecoverableSyncContractReason =
  (typeof RECOVERABLE_SYNC_CONTRACT_REASONS)[number];

export interface RecoverableContractEvent {
  kind: string;
  payload: Record<string, unknown>;
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recovery is deliberately narrow: only events whose old rejection is fully
 * explained by a now-fixed contract can be put back in the outbox. All other
 * terminal errors remain in the reconciliation ledger.
 */
export function isRecoverableSyncContractEvent(
  reason: string,
  value: unknown,
): value is RecoverableContractEvent {
  if (!record(value) || !record(value.payload) || typeof value.kind !== 'string') {
    return false;
  }
  if (reason === 'temperature_field_not_allowed:operational_time') {
    return value.kind === 'temperature_reading'
      && typeof value.payload.operational_time === 'string'
      && TIME_PATTERN.test(value.payload.operational_time);
  }
  if (reason === 'cleaning_field_not_allowed:operator_name') {
    const name = value.payload.operator_name;
    return value.kind === 'cleaning_done'
      && (name === null || typeof name === 'string')
      && 'operator_name' in value.payload;
  }
  if (reason === 'event_reference_business_mismatch:cleaning_products') {
    return value.kind === 'cleaning_done'
      && typeof value.payload.product_id === 'string'
      && UUID_PATTERN.test(value.payload.product_id);
  }
  if (reason === 'insert_failed:42501' || reason === 'insert_failed') {
    const assertion = value.operator_assertion;
    const signed = record(assertion)
      && typeof assertion.grant_id === 'string'
      && UUID_PATTERN.test(assertion.grant_id)
      && typeof assertion.tenant_personnel_id === 'string'
      && UUID_PATTERN.test(assertion.tenant_personnel_id)
      && typeof assertion.grant_token === 'string'
      && /^[0-9a-f]{64}$/.test(assertion.grant_token);
    const current = value.payload.recorded_late === undefined
      && value.payload.operational_date === undefined
      && value.payload.operational_time === undefined;
    const late = value.payload.recorded_late === true
      && typeof value.payload.operational_date === 'string'
      && DATE_PATTERN.test(value.payload.operational_date)
      && typeof value.payload.operational_time === 'string'
      && TIME_PATTERN.test(value.payload.operational_time);
    return (value.kind === 'temperature_reading' || value.kind === 'cleaning_done')
      && signed
      && (current || late);
  }
  return false;
}

export interface ConfirmedCleaningRecoveryEvent {
  kind: string;
  id: string;
  server_id: string;
  tenant_id: string;
  business_id: string;
  client_ts: string;
  rev: number;
  self_hash: string;
  payload: Record<string, unknown>;
}

/** Match an activity, including the same employee using a personal account.
 * Equal temperatures or the same day alone never establish equivalence. */
export function confirmedEquivalentCleaning(
  original: unknown,
  confirmed: readonly ConfirmedCleaningRecoveryEvent[],
): ConfirmedCleaningRecoveryEvent | null {
  if (!record(original) || original.kind !== 'cleaning_done'
    || !record(original.payload) || original.payload.recorded_late !== true
    || !record(original.operator_assertion)
    || typeof original.operator_assertion.tenant_personnel_id !== 'string'
    || typeof original.client_ts !== 'string'
    || !Number.isFinite(Date.parse(original.client_ts))) return null;
  if (typeof original.tenant_id !== 'string' || !UUID_PATTERN.test(original.tenant_id)
    || typeof original.business_id !== 'string' || !UUID_PATTERN.test(original.business_id)) return null;
  const personId = original.operator_assertion.tenant_personnel_id;
  if (!UUID_PATTERN.test(personId)) return null;
  const domain = (payload: Record<string, unknown>): Record<string, unknown> => {
    const result = { ...payload };
    delete result._operator;
    delete result._operational;
    // These can be the technical tablet account and the employee's account.
    // Only server-attested personnel identity below can bridge them.
    delete result.performed_by_user_id;
    return result;
  };
  return confirmed.find((event) => {
    if (event.kind !== 'cleaning_done' || !record(event.payload)) return false;
    const operator = event.payload._operator;
    const operational = event.payload._operational;
    const person = record(operator) ? operator.tenant_personnel_id : undefined;
    const operationalPerson = record(operational) ? operational.tenant_personnel_id : undefined;
    return event.tenant_id === original.tenant_id && event.business_id === original.business_id
      && event.rev > 0 && UUID_PATTERN.test(event.server_id)
      && /^[0-9a-f]{64}$/.test(event.self_hash)
      && (person === personId || operationalPerson === personId)
      && (person === undefined || person === personId)
      && (operationalPerson === undefined || operationalPerson === personId)
      && Date.parse(event.client_ts) === Date.parse(original.client_ts as string)
      && deepEqualJson(domain(event.payload), domain(original.payload as Record<string, unknown>));
  }) ?? null;
}
